import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Navigation, ChevronDown } from 'lucide-react';
import Logo from '@/components/Logo';
import { useAuth } from '@/context/AuthContext';

const CitySelection = () => {
  const navigate = useNavigate();
  const { setUserCity } = useAuth();
  const [selectedCity, setSelectedCity] = useState('Hassan');
  const [loading, setLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [error, setError] = useState('');
  const [showLocationModal, setShowLocationModal] = useState(false);

  // Available cities (Hassan is live, others coming soon)
  const cities = [
    { id: 'hassan', name: 'Hassan', live: true, coords: { lat: 13.0033, lng: 76.1004 }, radius: 50 }, // 50km radius
    { id: 'bangalore', name: 'Bangalore', live: false, coords: { lat: 12.9716, lng: 77.5946 }, radius: 50 },
    { id: 'mysore', name: 'Mysore', live: false, coords: { lat: 12.2958, lng: 76.6394 }, radius: 50 },
    { id: 'mangalore', name: 'Mangalore', live: false, coords: { lat: 12.9141, lng: 74.8560 }, radius: 50 },
  ];

  // Calculate distance between two coordinates using Haversine formula (in km)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Radius of Earth in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    return distance;
  };

  // Check if location is within Hassan district
  const isWithinHassan = (latitude, longitude) => {
    const hassanCity = cities.find(city => city.id === 'hassan');
    const distance = calculateDistance(
      latitude,
      longitude,
      hassanCity.coords.lat,
      hassanCity.coords.lng
    );
    console.log('Distance from Hassan center:', distance.toFixed(2), 'km');
    return distance <= hassanCity.radius;
  };

  // Get current location using browser geolocation
  const handleUseCurrentLocation = () => {
    setLocationLoading(true);
    setError('');

    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser. Please select your city manually.');
      setLocationLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        console.log('Current location:', latitude, longitude);
        
        // Check if user is within Hassan district
        if (isWithinHassan(latitude, longitude)) {
          setSelectedCity('Hassan');
          setLocationLoading(false);
          
          // Show success message briefly
          const successMsg = document.createElement('div');
          successMsg.className = 'fixed top-4 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in';
          successMsg.textContent = '✓ Location detected: Hassan';
          document.body.appendChild(successMsg);
          
          setTimeout(() => {
            successMsg.remove();
            handleContinue();
          }, 1500);
        } else {
          // User is outside Hassan district
          setLocationLoading(false);
          setError('Your location is outside Hassan district. Please select your city manually to see available offers.');
        }
      },
      (err) => {
        console.error('Location error:', err);
        let errorMessage = 'Unable to get your location. ';
        
        if (err.code === 1) {
          // Permission denied - show modal with instructions
          setShowLocationModal(true);
          errorMessage += 'Location permission denied.';
        } else if (err.code === 2) {
          errorMessage += 'Location unavailable. Please select your city manually.';
        } else if (err.code === 3) {
          errorMessage += 'Request timed out. Please select your city manually.';
        } else {
          errorMessage += 'Please select your city manually.';
        }
        
        setError(errorMessage);
        setLocationLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  // Continue to home
  const handleContinue = () => {
    if (!selectedCity) {
      setError('Please select a city');
      return;
    }

    setLoading(true);
    
    // Save city to context and localStorage
    setUserCity(selectedCity);
    localStorage.setItem('userCity', selectedCity);
    
    // Navigate to home
    setTimeout(() => {
      navigate('/home');
    }, 500);
  };

  return (
    <div 
      className="min-h-screen flex flex-col items-center justify-center p-4"
      style={{
        background: 'linear-gradient(135deg, #43A047 0%, #66BB6A 25%, #9CCC65 50%, #CDDC39 75%, #FDD835 100%)'
      }}
      data-testid="city-selection-page"
    >
      {/* Logo */}
      <div className="mb-6 animate-fade-in">
        <Logo size="small" />
      </div>

      {/* City Selection Card */}
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-8 animate-fade-in" style={{ animationDelay: '0.2s' }}>
        <h1 className="text-2xl font-bold text-gray-800 text-center mb-2" data-testid="city-selection-heading">
          Select Your City
        </h1>
        <p className="text-center text-sm text-gray-600 mb-6">
          Currently live in <span className="font-semibold text-green-600">Hassan</span>. Coming to more cities soon!
        </p>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm text-center" data-testid="error-message">
            {error}
          </div>
        )}

        {/* Map Display */}
        <div className="mb-6 rounded-2xl overflow-hidden shadow-lg border-4 border-gray-100">
          <div className="relative w-full h-64 bg-gray-200">
            {/* Static map showing Hassan - Using OpenStreetMap static image */}
            <iframe
              title="Hassan Map"
              width="100%"
              height="100%"
              frameBorder="0"
              scrolling="no"
              marginHeight="0"
              marginWidth="0"
              src="https://www.openstreetmap.org/export/embed.html?bbox=76.0504,12.9533,76.1504,13.0533&layer=mapnik&marker=13.0033,76.1004"
              style={{ border: 0 }}
            ></iframe>
            
            {/* Location Pin Overlay */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none">
              <MapPin size={48} className="text-red-500 drop-shadow-lg" fill="#ef4444" />
            </div>
          </div>
        </div>

        {/* Use Current Location Button */}
        <button
          onClick={handleUseCurrentLocation}
          disabled={locationLoading}
          className="w-full py-3 mb-4 rounded-xl font-semibold text-gray-700 bg-white border-2 border-green-500 hover:bg-green-50 transition-all duration-300 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="use-location-button"
        >
          <Navigation size={20} className={locationLoading ? 'animate-spin' : ''} />
          <span>{locationLoading ? 'Getting Location...' : 'Use My Current Location'}</span>
        </button>

        {/* OR Divider */}
        <div className="flex items-center my-4">
          <div className="flex-1 border-t border-gray-300"></div>
          <span className="px-4 text-gray-500 text-sm font-medium">OR</span>
          <div className="flex-1 border-t border-gray-300"></div>
        </div>

        {/* Manual City Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select City Manually
          </label>
          <div className="relative">
            <select
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
              className="w-full px-4 py-3 pr-10 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent appearance-none bg-white text-gray-700 font-medium"
              data-testid="city-dropdown"
            >
              {cities.map((city) => (
                <option key={city.id} value={city.name} disabled={!city.live}>
                  {city.name} {city.live ? '✓ Live' : '(Coming Soon)'}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={20} />
          </div>
        </div>

        {/* Continue Button */}
        <button
          onClick={handleContinue}
          disabled={loading || !selectedCity}
          className="w-full py-3 rounded-xl font-semibold text-white shadow-lg transition-all duration-300 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            background: 'linear-gradient(90deg, #43A047 0%, #66BB6A 50%, #9CCC65 100%)'
          }}
          data-testid="continue-home-button"
        >
          {loading ? 'Loading...' : 'Continue to Home'}
        </button>

        {/* Info Text */}
        <p className="text-xs text-gray-500 text-center mt-4">
          More cities coming soon! Stay tuned.
        </p>
      </div>

      {/* Location Permission Modal */}
      {showLocationModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 animate-fade-in" data-testid="location-modal">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <MapPin size={32} className="text-red-500" />
              </div>
            </div>

            {/* Title */}
            <h2 className="text-xl font-bold text-gray-800 text-center mb-2">
              Location Access Needed
            </h2>

            {/* Message */}
            <p className="text-gray-600 text-center mb-4 text-sm">
              MyLocalGo needs your location to show nearby offers and deals.
            </p>

            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm font-semibold text-blue-900 mb-2">
                How to enable location:
              </p>
              <ol className="text-xs text-blue-800 space-y-1 list-decimal list-inside">
                <li>Look for the location icon <span className="inline-block">🔒</span> in your browser&apos;s address bar</li>
                <li>Click on it and select &quot;Allow&quot; for location</li>
                <li>Refresh the page or click &quot;Try Again&quot; below</li>
              </ol>
              
              <div className="mt-3 pt-3 border-t border-blue-200">
                <p className="text-xs text-blue-800 font-semibold mb-1">Browser-specific:</p>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li>• <span className="font-medium">Chrome/Edge:</span> Settings → Privacy → Location</li>
                  <li>• <span className="font-medium">Safari:</span> Settings → Websites → Location Services</li>
                  <li>• <span className="font-medium">Firefox:</span> Settings → Privacy → Permissions → Location</li>
                </ul>
              </div>
            </div>

            {/* Buttons */}
            <div className="space-y-2">
              <button
                onClick={() => {
                  setShowLocationModal(false);
                  setError('');
                  handleUseCurrentLocation();
                }}
                className="w-full py-3 rounded-xl font-semibold text-white shadow-lg transition-all duration-300 hover:shadow-xl"
                style={{
                  background: 'linear-gradient(90deg, #43A047 0%, #66BB6A 50%, #9CCC65 100%)'
                }}
                data-testid="try-again-button"
              >
                Try Again
              </button>
              <button
                onClick={() => {
                  setShowLocationModal(false);
                  setError('');
                }}
                className="w-full py-3 rounded-xl font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-all duration-300"
                data-testid="select-manually-button"
              >
                Select City Manually
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CitySelection;